import React, { useState, useEffect } from "react";
import { supabase } from "../../services/supabase";

// Shows recent registrations from BOTH sources:
//  - user_profiles          → website (email / WhatsApp) signups
//  - whatsapp_referral_users → WhatsApp bot signups (this is where AD signups land)
// Gives a combined, filterable, most-recent-first view with attribution.

type Row = {
  id: string;
  name: string;
  contact: string;          // email or phone
  source: string;           // website | wa_bot | ad_competition | ad_product | ad_generic | referral
  consent: boolean | null;
  referralCode: string | null;
  referralCount: number | null;
  createdAt: string | null;
  origin: "profile" | "bot";
};

const sourceLabel: Record<string, string> = {
  website: "Website",
  wa_bot: "WhatsApp Bot",
  ad_competition: "Ad · Prize (WIN2500)",
  ad_product: "Ad · Product (TYRES)",
  ad_generic: "Ad · Generic (JOIN)",
  referral: "Referral",
};

function sourceColor(source: string): string {
  if (source.startsWith("ad_")) return "bg-purple-100 text-purple-800";
  if (source === "referral") return "bg-green-100 text-green-800";
  if (source === "website") return "bg-blue-100 text-blue-800";
  return "bg-gray-100 text-gray-700";
}

const UsersRegistrationsTab: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "ads" | "referral" | "website">("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // Website / app accounts
      const { data: profiles, error: pErr } = await supabase
        .from("user_profiles")
        .select("id, full_name, email, phone, whatsapp_number, marketing_consent, created_at")
        .order("created_at", { ascending: false })
        .limit(500);
      if (pErr) throw pErr;

      // WhatsApp bot / competition entrants (where ad signups land)
      const { data: refUsers, error: rErr } = await supabase
        .from("whatsapp_referral_users")
        .select("id, name, phone_number, signup_source, referred_by_code, marketing_consent, referral_code, referral_count, registered, created_at")
        .order("created_at", { ascending: false })
        .limit(500);
      if (rErr) throw rErr;

      const profileRows: Row[] = (profiles || []).map((p: any) => ({
        id: p.id,
        name: p.full_name || "(no name)",
        contact: p.email || p.phone || p.whatsapp_number || "—",
        source: "website",
        consent: p.marketing_consent ?? null,
        referralCode: null,
        referralCount: null,
        createdAt: p.created_at,
        origin: "profile" as const,
      }));

      const botRows: Row[] = (refUsers || [])
        .filter((r: any) => r.registered)
        .map((r: any) => ({
          id: r.id,
          name: r.name || "(no name)",
          contact: r.phone_number || "—",
          source: r.signup_source || (r.referred_by_code ? "referral" : "wa_bot"),
          consent: r.marketing_consent ?? null,
          referralCode: r.referral_code || null,
          referralCount: r.referral_count ?? 0,
          createdAt: r.created_at,
          origin: "bot" as const,
        }));

      const combined = [...profileRows, ...botRows].sort((a, b) => {
        const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return tb - ta;
      });
      setRows(combined);
    } catch (e: any) {
      setError(e?.message || "Failed to load registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => {
    if (filter === "ads" && !r.source.startsWith("ad_")) return false;
    if (filter === "referral" && r.source !== "referral") return false;
    if (filter === "website" && r.source !== "website") return false;
    if (search) {
      const q = search.toLowerCase();
      if (!r.name.toLowerCase().includes(q) && !r.contact.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // Quick stats
  const total = rows.length;
  const adTotal = rows.filter((r) => r.source.startsWith("ad_")).length;
  const referralTotal = rows.filter((r) => r.source === "referral").length;
  const last24h = rows.filter((r) => r.createdAt && Date.now() - new Date(r.createdAt).getTime() < 86400000).length;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Users & Registrations</h2>
          <p className="text-gray-600 mt-1">New sign-ups from the website and WhatsApp, including ad campaign attribution.</p>
        </div>
        <button
          onClick={load}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
        >
          Refresh
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total registered", value: total },
          { label: "New (24h)", value: last24h },
          { label: "From ads", value: adTotal },
          { label: "From referrals", value: referralTotal },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-lg border p-4">
            <div className="text-2xl font-bold text-gray-900">{loading ? "…" : s.value}</div>
            <div className="text-sm text-gray-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex rounded-lg border overflow-hidden">
          {(["all", "ads", "referral", "website"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm capitalize ${
                filter === f ? "bg-blue-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search name or number…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[200px] border rounded-lg px-3 py-2 text-sm"
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4 text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Contact</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Consent</th>
                <th className="px-4 py-3 font-medium">Referral code</th>
                <th className="px-4 py-3 font-medium">Referrals</th>
                <th className="px-4 py-3 font-medium">Registered</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No registrations yet.</td></tr>
              ) : (
                filtered.map((r) => (
                  <tr key={`${r.origin}-${r.id}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-3 text-gray-600">{r.contact}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${sourceColor(r.source)}`}>
                        {sourceLabel[r.source] || r.source}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {r.consent === true ? (
                        <span className="text-green-600">Yes</span>
                      ) : r.consent === false ? (
                        <span className="text-gray-400">No</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{r.referralCode || "—"}</td>
                    <td className="px-4 py-3 text-gray-600">{r.referralCount != null ? r.referralCount : "—"}</td>
                    <td className="px-4 py-3 text-gray-500">
                      {r.createdAt ? new Date(r.createdAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs text-gray-400 mt-3">
        Showing up to 500 most recent from each source. Ad signups appear as “Ad · …” based on the code in each ad.
      </p>
    </div>
  );
};

export default UsersRegistrationsTab;
